import { type Dayjs } from "dayjs"
import { type ReactNode } from "react"

import { CLASS_GROUPS } from "#constants/classOptions"
import {
  type CoreSkillLevel,
  type DigitalSkillLevel,
  type SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { StudentSkillType } from "#customTypes/composedTypes/studentTypes"
import { type FirebaseDocRef, type Uuid } from "#customTypes/globalTypes"
import {
  Address,
  type DatabaseObject,
  type FormData,
  IsoDate,
  type TableData,
} from "#customTypes/types"
import { type FeeBreakdown } from "#utils/feeCalculationHelpers"
import { type TryoutResult } from "#utils/tryoutHelpers"

/**
 * Step 2's three level dropdowns, named for the ladder each one edits.
 * These are form fields on the tryout doc, not student fields — the chosen
 * level reaches the student as a `skillHistory` entry.
 */
export const STEP_2_SKILL_FIELD_BY_TYPE = {
  [StudentSkillType.core]: "coreSkillLevel",
  [StudentSkillType.digital]: "digitalSkillLevel",
  [StudentSkillType.sculpture]: "sculptureSkillLevel",
} as const

import { ClassSessionConstructor } from "./classSessionTypes"
import { UploadFile } from "./firestoreTypes"
import { StudentConstructor } from "./studentTypes"

export enum TryoutStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}
export type TryoutConstructor = DatabaseObject & {
  id: Uuid
  studentId: Uuid
  semester: string | undefined
  sessionId: Uuid
  tryoutFilterDate: string

  tryoutStatus: TryoutStatus

  step1: {
    isTryoutFeePaid: boolean
    isTourCompleted: boolean
    isEvalFormCompleted: boolean
    hasEmergencyContact: boolean

    tryoutFeeId?: Uuid
    tryoutDiscountIds?: Uuid[]

    // Comes from student
    // primaryAddress?: Address
    // guardian1Name?: string
    // guardian1Phone?: string
    // guardian1Email?: string
    // guardian2Name?: string
    // guardian2Phone?: string
    // guardian2Email?: string
  }
  step2: {
    isEvalScanned: boolean
    isLevelUpdated: boolean
    hasRecommendedClass: boolean
    didPassTryout?: boolean // undefined = not yet decided (see getTryoutResult)

    uploadedEval?: UploadFile
    recommendedClassGroup?: CLASS_GROUPS
    // coreSkillLevel?: CoreSkillLevel
    // digitalSkillLevel?: DigitalSkillLevel
    recommendedClassId?: Uuid
    recommendedSemester?: string
    recommendedTeacherId?: Uuid
    recommendedSessionIds?: Uuid[]
  }
  step3: {
    hasEvalFeedback: boolean
    isFollowupEmailSent: boolean
    hasSelectedSessions: boolean

    selectedSessionIds?: Uuid[]
    selectedClassId?: Uuid
    followupEmail?: string
  }
}

export type NewTryoutConstructor = DatabaseObject & {
  id: Uuid
  studentId: Uuid
  semester: string | undefined
  sessionId: Uuid
  tryoutFilterDate: string

  tryoutStatus: TryoutStatus

  step0: {
    isTryoutFeePaid: boolean
    hasSelectedSession: boolean
    tryoutFeeId?: Uuid
    tryoutDiscountIds?: Uuid[]

    selectedSessionId?: Uuid
    semesterFilter?: string
    teacherFilter?: Uuid
    classGroupFilter?: CLASS_GROUPS[]
  }
  step1: {
    // isTryoutFeePaid: boolean // Removed
    isTourCompleted: boolean
    // isEvalFormCompleted: boolean // Removed
    teacherHasEvalForm: boolean
    hasEmergencyContact: boolean
    hasSignedReleaseForm: boolean

    uploadedReleaseForm?: string // URL of uploaded signed release form

    // Comes from student
    // primaryAddress?: Address
    // guardian1Name?: string
    // guardian1Phone?: string
    // guardian1Email?: string
    // guardian2Name?: string
    // guardian2Phone?: string
    // guardian2Email?: string
  }
  step2: {
    isEvalScanned: boolean
    isLevelUpdated: boolean
    didPassTryout?: boolean // Whether student passed the tryout eval (undefined = not yet selected)

    uploadedEval?: UploadFile
    failureReason?: string // If didPassTryout is false, reason for failure
  }
  step3: {
    hasEvalFeedback: boolean // Teacher reviewed eval with parent
    hasGivenSwag: boolean // Swag given (button, comic book, etc.)
    hasSelectedSessions: boolean

    selectedClassId?: Uuid // Class selected for enrollment
    selectedSessionIds?: Uuid[] // Sessions selected for enrollment
    baselineEnrollmentByClass?: Record<Uuid, number> // Baseline enrollment count per class when first selected
    // When true the enrollment class picker lists next semester's classes
    // (addOneSemester(tryout.semester)) instead of the tryout semester's.
    // Mirrors makeup.isScheduledForNextSemester. Absent/false = current.
    isEnrollingNextSemester?: boolean

    // Receipt-related fields (replaces followup email)
    sendReceipt?: boolean // Whether to send enrollment receipt on submit
    enrollmentReceiptSent?: boolean // True if an enrollment receipt has already been sent
    scheduledEnrollmentEmail?: ScheduledEnrollmentEmail | null
    feeBreakdown?: FeeBreakdown // Calculated fee breakdown
    paymentAmount?: string // Formatted total (e.g., "$605")
    optOutDates?: string // Skipped session dates
    siblingDiscount?: boolean
    extraClassDiscount?: boolean
  }
}

export type ScheduledEnrollmentEmail = {
  mandrillIds: string[]
  sendAt: string // ISO timestamp of the moment Mandrill will send
}

export type TryoutForTable = TableData &
  Omit<TryoutConstructor, "studentId" | "classId" | "sessionId"> & {
    id: Uuid
    studentName: string
    // Display value (DOB + name); kept for search. Sorting keys off studentName.
    studentNameAndDob: string
    /** MMYY formatted DOB — displayed as its own column. */
    studentDob: string
    semester: string
    className: string
    startDate: string
    startTime: string
    endTime: string
    createdAt: string
    progress: ReactNode
    // Epoch ms of the session start; hidden sort backing for startDate/startTime.
    eventStart: number
    /** Pass/fail outcome (TRYOUT_RESULTS); rendered via TryoutResultTag. */
    result: TryoutResult
    // Hidden sort backing for result (Pending → Passed → Failed).
    resultRank: number
  }

export type TryoutRef = FirebaseDocRef<TryoutConstructor>

export interface AddTryoutFormValues extends FormData {
  id: Uuid | undefined
  studentId: Uuid | undefined
  semester: string | undefined
  classId?: Uuid
  sessionId: Uuid | undefined
  tryoutFilterDate: Dayjs | undefined

  step0?: {
    isTryoutFeePaid: boolean
    hasSelectedSession: boolean
    tryoutFeeId?: Uuid
  }
  receipt?: {
    studentName: string
    studentDob: string
    classLabel: string
  }
}
export interface AddTryoutFormSubmitValues extends FormData {
  id: Uuid | undefined
  studentId: Uuid
  semester: string
  sessionId: Uuid
  tryoutFilterDate: IsoDate
  step0: {
    isTryoutFeePaid: boolean
    tryoutFeeId?: Uuid
  }
}

export interface TryoutFee {
  id: Uuid
  price: number
  label: string
  description: string
}

export interface TryoutDiscount {
  id: Uuid
  discount: number
  label: string
  description: string
  isActive: boolean
}

// Don't forget that a new progress form is not the same as a new tryout.
export interface NewTryoutProgressFormData extends FormData {
  // These should already be filled out from the initial AddTryoutForm
  id: Uuid
  studentId: Uuid
  semester: string
  sessionId: Uuid
  tryoutFilterDate: Dayjs

  tryoutStatus: TryoutStatus

  step0?: {
    isTryoutFeePaid?: boolean
    hasSelectedSession?: boolean
    tryoutFeeId?: Uuid
    tryoutDiscountIds?: Uuid[]
  }
  step1?: {
    isTourCompleted?: boolean
    teacherHasEvalForm?: boolean
    hasEmergencyContact?: boolean
    hasSignedReleaseForm?: boolean

    uploadedReleaseForm?: string // URL of uploaded signed release form

    guardian1Name?: string
    guardian1Phone?: string
    guardian1Email?: string
    guardian2Name?: string
    guardian2Phone?: string
    guardian2Email?: string
    primaryAddress?: Address
  }
  step2?: {
    isEvalScanned?: boolean
    isLevelUpdated?: boolean
    didPassTryout?: boolean

    uploadedEval?: UploadFile
    failureReason?: string
    coreSkillLevel?: CoreSkillLevel
    digitalSkillLevel?: DigitalSkillLevel
    sculptureSkillLevel?: SculptureSkillLevel
  }
  step3?: {
    hasEvalFeedback?: boolean
    hasGivenSwag?: boolean
    hasSelectedSessions?: boolean

    selectedClassId?: Uuid
    selectedSessionIds?: Uuid[]
    baselineEnrollmentByClass?: Record<Uuid, number>
    isEnrollingNextSemester?: boolean

    // Receipt-related fields (replaces followup email)
    sendReceipt?: boolean
    enrollmentReceiptSent?: boolean
    scheduledEnrollmentEmail?: ScheduledEnrollmentEmail | null
    feeBreakdown?: FeeBreakdown
    paymentAmount?: string
    optOutDates?: string
    siblingDiscount?: boolean
    extraClassDiscount?: boolean
  }
}

// All step completion booleans should be defined after first submit
export interface EditTryoutProgressFormData extends FormData {
  id: Uuid
  studentId: Uuid
  semester: string
  sessionId: Uuid
  tryoutFilterDate: Dayjs

  tryoutStatus: TryoutStatus

  step0: {
    isTryoutFeePaid: boolean
    hasSelectedSession: boolean
    tryoutFeeId?: Uuid
    tryoutDiscountIds?: Uuid[]
  }
  step1: {
    isTourCompleted: boolean
    teacherHasEvalForm: boolean
    hasEmergencyContact: boolean
    hasSignedReleaseForm: boolean

    uploadedReleaseForm?: UploadFile

    primaryAddress?: Address
    guardian1Name?: string
    guardian1Phone?: string
    guardian1Email?: string
    guardian2Name?: string
    guardian2Phone?: string
    guardian2Email?: string
  }
  step2: {
    isEvalScanned: boolean
    isLevelUpdated: boolean
    didPassTryout?: boolean // undefined = not yet selected

    uploadedEval?: UploadFile
    failureReason?: string
    coreSkillLevel?: CoreSkillLevel
    digitalSkillLevel?: DigitalSkillLevel
    sculptureSkillLevel?: SculptureSkillLevel
  }
  step3: {
    hasEvalFeedback: boolean
    hasGivenSwag: boolean
    hasSelectedSessions: boolean

    selectedClassId?: Uuid
    selectedSessionIds?: Uuid[]
    baselineEnrollmentByClass?: Record<Uuid, number>
    isEnrollingNextSemester?: boolean

    // Receipt-related fields (replaces followup email)
    sendReceipt?: boolean
    enrollmentReceiptSent?: boolean
    scheduledEnrollmentEmail?: ScheduledEnrollmentEmail | null
    feeBreakdown?: FeeBreakdown
    paymentAmount?: string
    optOutDates?: string
    siblingDiscount?: boolean
    extraClassDiscount?: boolean
  }
}

// Union type for form data
export type TryoutProgressFormData =
  | NewTryoutProgressFormData
  | EditTryoutProgressFormData

// Type guard to check if we're editing
export const isEditTryout = (
  data: TryoutProgressFormData
): data is EditTryoutProgressFormData => {
  return "id" in data && data.id !== undefined
}

export interface TryoutProgressSubmitData {
  student: StudentConstructor
  tryoutClassSession: ClassSessionConstructor
  tryout: TryoutProgressFormData
}

// // Type for submitted data (all required fields)
// export interface TryoutProgressSubmitData {
//   id?: Uuid
//   studentId: Uuid
//   classId: Uuid
//   sessionId: Uuid
//   tryoutFilterDate: string

//   // These are added by cleanData() before we format for submit
//   createdAt: string
//   createdBy: Uuid
//   lastUpdatedAt: string | undefined
//   lastUpdatedBy: Uuid | undefined

//   status: TryoutStatus

//   // step 1
//   isTryoutFeePaid: boolean
//   isTourCompleted: boolean
//   isEvalFormCompleted: boolean
//   hasEmergencyContact: boolean

//   tryoutFeeId?: Uuid
//   tryoutDiscountIds?: Uuid[]

//   // This gets pulled out and saved to the student
//   primaryAddress?: Address
//   guardian1Name?: string
//   guardian1Phone?: string
//   guardian1Email?: string
//   guardian2Name?: string
//   guardian2Phone?: string
//   guardian2Email?: string

//   // step 2
//   isEvalScanned: boolean
//   isLevelUpdated: boolean
//   hasRecommendedClass: boolean

//   uploadedEvals?: UploadFile[]
//   recommendedClassGroup?: CLASS_GROUPS
//   recommendedSkillLevel?: CoreSkillLevel | DigitalSkillLevel
//   recommendedSemester?: string
//   recommendedTeacherId?: Uuid
//   recommendedClassId?: Uuid
//   recommendedSessionIds?: Uuid[]

//   // step 3
//   hasEvalFeedback: boolean
//   isFollowupEmailSent: boolean
//   hasSelectedSessions: boolean

//   followupEmail?: string
// }
